import { useState } from "react";
import { useWorkouts, transformWorkouts } from "../hooks/useApi";
import storage from "../utils/storage";
import { computeStats, getAllPersonalRecords, getAllCheckIns, getWorkoutHistory, computeReadinessScore, computeFatigueScore } from "../utils/analytics";

// ══════════════════════════════════════════════════════════════
// FORGE DOCUMENTS HUB — Client PDF Generation Center
// Program PDF, Progress Reports, Individual Workout Sheets
// ══════════════════════════════════════════════════════════════

export default function DocumentsView({ C, onBack, onNav }) {
  const { workouts: apiWorkouts } = useWorkouts();
  const DAYS = transformWorkouts(apiWorkouts);
  const stats = computeStats();
  const prs = getAllPersonalRecords();
  const checkIns = getAllCheckIns();
  const userName = storage.get("user_name", "Athlete");
  const [generating, setGenerating] = useState(null);
  const [selectedWorkout, setSelectedWorkout] = useState(null);

  const allTrainingDays = DAYS.filter(d => !d.rest);

  // ─── SHARED PDF STYLES ──────────────────────────────
  const pdfStyles = (ac) => `
    @page { margin: 0.75in 0.6in; size: letter; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Inter', -apple-system, sans-serif; color: #1a1a2e; background: #fff; max-width: 760px; margin: 0 auto; font-size: 12px; line-height: 1.6; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    h1 { font-family: 'Cinzel', serif; font-size: 36px; font-weight: 900; letter-spacing: 0.18em; margin-bottom: 2px; color: #0a0a1a; line-height: 1.2; }
    h2 { font-family: 'Cinzel', serif; font-size: 17px; font-weight: 700; letter-spacing: 0.14em; color: ${ac}; margin: 36px 0 14px; padding-bottom: 8px; border-bottom: 2px solid ${ac}18; position: relative; page-break-after: avoid; }
    h2::before { content: ''; position: absolute; bottom: -2px; left: 0; width: 40px; height: 2px; background: ${ac}; }
    h3 { font-family: 'JetBrains Mono', monospace; font-size: 10.5px; font-weight: 700; letter-spacing: 0.18em; color: ${ac}; margin: 22px 0 8px; text-transform: uppercase; }
    h4 { font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 700; color: #1a1a2e; margin: 14px 0 6px; }
    .subtitle { font-family: 'JetBrains Mono', monospace; font-size: 9.5px; letter-spacing: 0.22em; color: ${ac}; margin-bottom: 2px; text-transform: uppercase; }
    .meta { font-size: 10.5px; color: #666; margin-bottom: 3px; font-family: 'JetBrains Mono', monospace; letter-spacing: 0.05em; }
    .meta-label { color: #999; }
    .meta-value { color: #333; font-weight: 600; }
    .divider { height: 1px; background: linear-gradient(90deg, transparent, ${ac}35, ${ac}, ${ac}35, transparent); margin: 28px 0; }
    .divider-light { height: 1px; background: linear-gradient(90deg, transparent, #ddd, transparent); margin: 18px 0; }
    .section { page-break-inside: avoid; margin-bottom: 18px; }
    table { width: 100%; border-collapse: collapse; margin: 8px 0 16px; font-size: 11px; }
    th { background: ${ac}0a; color: ${ac}; font-family: 'JetBrains Mono', monospace; font-size: 8.5px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; padding: 8px 10px; text-align: left; border-bottom: 1.5px solid ${ac}25; }
    td { padding: 7px 10px; border-bottom: 1px solid #eee; vertical-align: top; line-height: 1.55; }
    tr:last-child td { border-bottom: none; }
    .exercise-name { font-weight: 600; color: #1a1a2e; }
    .note { background: ${ac}06; border-left: 3px solid ${ac}; padding: 12px 16px; margin: 12px 0; font-size: 11.5px; line-height: 1.7; border-radius: 0 6px 6px 0; color: #444; }
    .note-title { font-weight: 700; font-size: 10px; letter-spacing: 0.08em; margin-bottom: 4px; color: ${ac}; font-family: 'JetBrains Mono', monospace; text-transform: uppercase; }
    .stat-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin: 12px 0; }
    .stat-box { background: #fafafe; border: 1px solid #eee; border-radius: 8px; padding: 12px 8px; text-align: center; }
    .stat-val { font-size: 20px; font-weight: 700; color: ${ac}; font-family: 'JetBrains Mono', monospace; }
    .stat-label { font-size: 7px; color: #999; font-family: 'JetBrains Mono', monospace; letter-spacing: 0.12em; margin-top: 3px; text-transform: uppercase; }
    .macro-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin: 12px 0 16px; }
    .macro-box { background: #f8f8fc; border: 1.5px solid #e4e4ee; border-radius: 8px; padding: 14px 8px; text-align: center; }
    .macro-val { font-size: 24px; font-weight: 800; color: ${ac}; font-family: 'JetBrains Mono', monospace; }
    .macro-label { font-size: 7.5px; color: #888; font-family: 'JetBrains Mono', monospace; letter-spacing: 0.14em; margin-top: 4px; text-transform: uppercase; }
    .bar-container { height: 8px; background: #eee; border-radius: 4px; margin: 4px 0 8px; overflow: hidden; }
    .bar-fill { height: 100%; border-radius: 4px; transition: width 0.3s; }
    .footer { margin-top: 48px; padding-top: 16px; border-top: 1.5px solid #ddd; font-size: 9px; color: #aaa; font-family: 'JetBrains Mono', monospace; letter-spacing: 0.08em; text-align: center; line-height: 2; }
    @media print { body { padding: 0; } h2 { page-break-after: avoid; } .section { page-break-inside: avoid; } }
  `;

  const esc = (s) => (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  const fontLink = '<link href="https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700;800;900&family=Inter:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@300;400;500;600;700&display=swap" rel="stylesheet">';

  const openPrintWindow = (title, ac, buildHtml) => {
    const w = window.open("", "_blank");
    if (!w) { alert("Pop-up blocked. Please allow pop-ups to generate PDFs."); setGenerating(null); return null; }
    let html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><title>${esc(title)}</title>${fontLink}<style>${pdfStyles(ac)}</style></head><body>`;
    html += buildHtml();
    html += `</body></html>`;
    w.document.write(html);
    w.document.close();
    setTimeout(() => { w.print(); setGenerating(null); }, 1500);
    return w;
  };

  // ─── PROGRESS REPORT PDF ────────────────────────────
  const generateProgressReport = () => {
    setGenerating("progress");
    const ac = C.accent;
    const now = new Date();
    const dateStr = now.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    const history = getWorkoutHistory();
    const readiness = computeReadinessScore();
    const fatigue = computeFatigueScore();
    const weightEntries = checkIns.filter(ci => ci.wt && Number(ci.wt) > 0);
    const tier = storage.get("user_tier", "FORGE");
    const profile = storage.get("ob_data", {});

    openPrintWindow(`FORGE Progress Report — ${userName}`, ac, () => {
      let h = "";

      // Cover
      h += `<div style="text-align:center;margin-bottom:8px;">
        <div style="font-size:9px;color:${ac};font-family:'JetBrains Mono',monospace;letter-spacing:0.3em;margin-bottom:12px;">fitnessforge.ai</div>
        <h1>FORGE</h1>
        <div class="subtitle">Progress Report</div>
      </div>`;
      h += `<div class="divider"></div>`;

      // Client info
      h += `<div style="display:grid;grid-template-columns:1fr 1fr;gap:12px 32px;margin-bottom:4px;">`;
      h += `<div class="meta"><span class="meta-label">Prepared for: </span><span class="meta-value">${esc(userName)}</span></div>`;
      h += `<div class="meta"><span class="meta-label">Generated: </span><span class="meta-value">${dateStr}</span></div>`;
      h += `<div class="meta"><span class="meta-label">Plan: </span><span class="meta-value">${esc(tier)}</span></div>`;
      if (profile.goal) h += `<div class="meta"><span class="meta-label">Goal: </span><span class="meta-value">${esc(profile.goal)}</span></div>`;
      h += `</div>`;

      // Executive Summary
      h += `<h2>EXECUTIVE SUMMARY</h2>`;
      h += `<div class="stat-grid">`;
      h += `<div class="stat-box"><div class="stat-val">${stats.workoutCount}</div><div class="stat-label">WORKOUTS</div></div>`;
      h += `<div class="stat-box"><div class="stat-val">${stats.streak}</div><div class="stat-label">STREAK</div></div>`;
      h += `<div class="stat-box"><div class="stat-val">${stats.cyclesCompleted}</div><div class="stat-label">CYCLES</div></div>`;
      h += `<div class="stat-box"><div class="stat-val">${stats.checkInCount}</div><div class="stat-label">CHECK-INS</div></div>`;
      h += `</div>`;

      if (stats.totalVolumeAllTime > 0) {
        h += `<div class="meta"><span class="meta-label">Total Volume Moved: </span><span class="meta-value">${stats.totalVolumeAllTime.toLocaleString()} lbs</span></div>`;
      }
      if (readiness) h += `<div class="meta"><span class="meta-label">Current Readiness: </span><span class="meta-value">${readiness.score}/100 (${readiness.label})</span></div>`;
      if (fatigue) h += `<div class="meta"><span class="meta-label">Fatigue Level: </span><span class="meta-value">${fatigue.fatigue}/100 — ${fatigue.label}</span></div>`;

      // Adherence
      if (stats.workoutCount > 0) {
        const totalPossible = Math.max(stats.cyclesCompleted * 6, stats.workoutCount);
        const adherence = Math.min(100, Math.round((stats.workoutCount / totalPossible) * 100));
        h += `<h2>TRAINING ADHERENCE</h2>`;
        h += `<div class="section">`;
        h += `<h4>Workout Completion Rate</h4>`;
        h += `<div style="display:flex;align-items:center;gap:16px;margin:8px 0;">`;
        h += `<div style="flex:1;"><div class="bar-container"><div class="bar-fill" style="width:${adherence}%;background:${ac};"></div></div></div>`;
        h += `<div style="font-size:20px;font-weight:800;color:${ac};font-family:'JetBrains Mono',monospace;">${adherence}%</div>`;
        h += `</div>`;
        h += `<div class="meta">${stats.workoutCount} of ${totalPossible} scheduled workouts completed</div>`;
        h += `</div>`;
      }

      // Personal Records
      if (prs.length > 0) {
        h += `<h2>PERSONAL RECORDS</h2>`;
        h += `<div class="note"><div class="note-title">Achievement Unlocked</div>${prs.length} personal records tracked. These represent your peak performance markers across all exercises.</div>`;
        h += `<table><tr><th>Exercise</th><th>Best Weight</th><th>Est. 1RM</th><th>Max Volume</th></tr>`;
        prs.slice(0, 20).forEach(pr => {
          h += `<tr><td class="exercise-name">${esc(pr.exercise)}</td><td>${pr.value}</td><td>${pr.type.replace("Est 1RM: ", "")}</td><td>${pr.maxVolume ? pr.maxVolume.toLocaleString() + " lbs" : "—"}</td></tr>`;
        });
        h += `</table>`;
      }

      // Body Weight Trend
      if (weightEntries.length >= 2) {
        const first = weightEntries[0];
        const last = weightEntries[weightEntries.length - 1];
        const change = (Number(last.wt) - Number(first.wt)).toFixed(1);
        const dir = Number(change) > 0 ? "+" : "";
        h += `<h2>BODY COMPOSITION TRACKING</h2>`;
        h += `<div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;margin:12px 0;">`;
        h += `<div class="stat-box"><div class="stat-val">${first.wt}</div><div class="stat-label">STARTING (LBS)</div></div>`;
        h += `<div class="stat-box"><div class="stat-val">${last.wt}</div><div class="stat-label">CURRENT (LBS)</div></div>`;
        h += `<div class="stat-box"><div class="stat-val">${dir}${change}</div><div class="stat-label">NET CHANGE</div></div>`;
        h += `</div>`;

        // Weight timeline
        h += `<h3>Weight History</h3>`;
        h += `<table><tr><th>Date</th><th>Weight</th><th>Change</th></tr>`;
        weightEntries.slice(-15).forEach((entry, i, arr) => {
          const d = entry.date ? entry.date.toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—";
          const prev = i > 0 ? Number(arr[i - 1].wt) : Number(entry.wt);
          const diff = (Number(entry.wt) - prev).toFixed(1);
          const diffStr = i === 0 ? "—" : (Number(diff) > 0 ? "+" + diff : diff) + " lbs";
          h += `<tr><td>${d}</td><td style="font-family:'JetBrains Mono',monospace;font-weight:600;">${entry.wt} lbs</td><td style="font-family:'JetBrains Mono',monospace;font-size:10px;color:${Number(diff) > 0 ? "#e53935" : Number(diff) < 0 ? "#43a047" : "#888"};">${diffStr}</td></tr>`;
        });
        h += `</table>`;
      }

      // Volume Distribution
      if (stats.muscleVolume && stats.totalVolumeAllTime > 0) {
        const mv = stats.muscleVolume;
        const total = Object.values(mv).reduce((a, b) => a + b, 0);
        if (total > 0) {
          h += `<h2>VOLUME DISTRIBUTION</h2>`;
          h += `<div class="note"><div class="note-title">Training Balance Analysis</div>Volume distribution across muscle groups reveals training emphasis and potential imbalances. Ideally, push/pull ratios should be roughly equal, and no single muscle group should exceed 25% of total volume.</div>`;
          const sorted = Object.entries(mv).filter(([, v]) => v > 0).sort(([, a], [, b]) => b - a);
          sorted.forEach(([muscle, vol]) => {
            const pct = ((vol / total) * 100).toFixed(1);
            h += `<div style="margin-bottom:8px;">`;
            h += `<div style="display:flex;justify-content:space-between;margin-bottom:2px;">`;
            h += `<span style="font-size:11px;font-weight:600;text-transform:capitalize;">${muscle}</span>`;
            h += `<span style="font-size:10px;font-family:'JetBrains Mono',monospace;color:${ac};">${Math.round(vol).toLocaleString()} lbs (${pct}%)</span>`;
            h += `</div>`;
            h += `<div class="bar-container"><div class="bar-fill" style="width:${pct}%;background:${ac};"></div></div>`;
            h += `</div>`;
          });
        }
      }

      // Workout History Timeline
      if (history.length > 0) {
        h += `<h2>RECENT TRAINING LOG</h2>`;
        h += `<table><tr><th>Date</th><th>Workout</th><th>Duration</th><th>Volume</th><th>Exercises</th></tr>`;
        history.slice(-15).reverse().forEach(w => {
          const d = w.timestamp ? new Date(w.timestamp).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—";
          h += `<tr><td>${d}</td><td class="exercise-name">${esc(w.title || w.name || "Workout")}</td><td style="font-family:'JetBrains Mono',monospace;font-size:10px;">${w.duration || "—"}</td><td style="font-family:'JetBrains Mono',monospace;font-size:10px;">${w.totalVolume ? Math.round(w.totalVolume).toLocaleString() + " lbs" : "—"}</td><td style="font-size:10px;">${w.exerciseCount || "—"}</td></tr>`;
        });
        h += `</table>`;
      }

      // Footer
      h += `<div class="footer">
        Generated by FORGE — fitnessforge.ai<br>
        ${dateStr} · Progress Report v1.0<br>
        <span style="color:#ccc;">Personalized for ${esc(userName)}. Do not redistribute.</span>
      </div>`;
      return h;
    });
  };

  // ─── INDIVIDUAL WORKOUT PDF ─────────────────────────
  const generateWorkoutPDF = (day) => {
    setGenerating("workout-" + day.d);
    const ac = C.accent;
    const now = new Date();
    const dateStr = now.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

    openPrintWindow(`FORGE — ${day.t}`, ac, () => {
      let h = "";

      // Header
      h += `<div style="text-align:center;margin-bottom:8px;">
        <div style="font-size:9px;color:${ac};font-family:'JetBrains Mono',monospace;letter-spacing:0.3em;margin-bottom:12px;">fitnessforge.ai</div>
        <h1>FORGE</h1>
        <div class="subtitle">Workout Sheet</div>
      </div>`;
      h += `<div class="divider"></div>`;

      // Workout info
      h += `<div style="display:grid;grid-template-columns:1fr 1fr;gap:8px 32px;margin-bottom:16px;">`;
      h += `<div class="meta"><span class="meta-label">Athlete: </span><span class="meta-value">${esc(userName)}</span></div>`;
      h += `<div class="meta"><span class="meta-label">Date: </span><span class="meta-value">${dateStr}</span></div>`;
      h += `<div class="meta"><span class="meta-label">Workout: </span><span class="meta-value">${esc(day.t)}</span></div>`;
      h += `<div class="meta"><span class="meta-label">Duration: </span><span class="meta-value">~${day.m} min</span></div>`;
      h += `</div>`;

      if (day.notes) {
        h += `<div class="note"><div class="note-title">Coach Notes</div>${esc(day.notes)}</div>`;
      }

      // Exercise table with logging columns
      h += `<h2>${esc(day.t).toUpperCase()}</h2>`;
      h += `<table>`;
      h += `<tr><th style="width:24%">Exercise</th><th style="width:10%">Rx</th><th>Cues</th><th style="width:8%">Set 1</th><th style="width:8%">Set 2</th><th style="width:8%">Set 3</th><th style="width:8%">Set 4</th></tr>`;
      day.x.forEach(ex => {
        h += `<tr>`;
        h += `<td class="exercise-name">${esc(ex.n)}${ex.p ? `<div style="font-size:9px;color:#888;font-style:italic;margin-top:2px;">${esc(ex.p)}</div>` : ""}</td>`;
        h += `<td style="font-family:'JetBrains Mono',monospace;font-size:11px;">${esc(ex.s)}</td>`;
        h += `<td style="font-size:10px;color:#555;">${(ex.c || []).map(c => esc(c)).join(". ")}</td>`;
        // Empty cells for manual logging
        for (let i = 0; i < 4; i++) {
          h += `<td style="border:1px solid #ddd;min-height:30px;">&nbsp;</td>`;
        }
        h += `</tr>`;
      });
      h += `</table>`;

      // Notes section
      h += `<div style="margin-top:24px;">`;
      h += `<h3>SESSION NOTES</h3>`;
      h += `<div style="border:1px solid #ddd;border-radius:6px;min-height:120px;padding:12px;margin-top:8px;">`;
      h += `<div style="font-size:9px;color:#ccc;font-family:'JetBrains Mono',monospace;letter-spacing:0.1em;">RPE: _____ / 10 &nbsp;&nbsp; ENERGY: _____ / 10 &nbsp;&nbsp; SLEEP LAST NIGHT: _____ hrs</div>`;
      h += `</div></div>`;

      // Footer
      h += `<div class="footer">
        Generated by FORGE — fitnessforge.ai<br>
        ${dateStr} · Day ${day.d} Workout Sheet<br>
        <span style="color:#ccc;">Personalized for ${esc(userName)}. Do not redistribute.</span>
      </div>`;
      return h;
    });
  };

  // ─── RENDER ─────────────────────────────────────────
  const totalExercises = allTrainingDays.reduce((sum, d) => sum + d.x.length, 0);
  const hasData = stats.workoutCount > 0;

  return (
    <div style={{ minHeight: "100vh", background: C.bg, fontFamily: "var(--b)" }}>

      {/* ═══ HERO SECTION ═══ */}
      <div style={{
        position: "relative", overflow: "hidden",
        padding: "0 0 32px",
        background: `linear-gradient(180deg, ${C.accent}08 0%, transparent 70%)`,
      }}>
        {/* Atmospheric glow */}
        <div style={{
          position: "absolute", top: -60, right: -40, width: 200, height: 200,
          background: `radial-gradient(circle, ${C.accent}12 0%, transparent 70%)`,
          borderRadius: "50%", pointerEvents: "none",
        }} />
        <div style={{
          position: "absolute", top: 20, left: -60, width: 160, height: 160,
          background: `radial-gradient(circle, ${C.secondary}08 0%, transparent 70%)`,
          borderRadius: "50%", pointerEvents: "none",
        }} />

        {/* Back button */}
        <div style={{ padding: "20px 16px 0", position: "relative", zIndex: 2 }}>
          <button onClick={onBack} style={{
            background: "none", border: "none", color: C.accent,
            fontSize: "var(--fs-body)", fontFamily: "var(--m)", fontWeight: 600,
            cursor: "pointer", letterSpacing: ".06em", minHeight: 44,
            display: "flex", alignItems: "center", gap: "var(--gap-sm)",
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M15 18l-6-6 6-6" /></svg>
            BACK
          </button>
        </div>

        {/* Hero content */}
        <div style={{ padding: "24px 20px 0", position: "relative", zIndex: 2 }}>
          <div style={{
            fontSize: 8, fontFamily: "var(--m)", fontWeight: 700,
            letterSpacing: ".28em", color: C.accent, marginBottom: 10,
            textTransform: "uppercase",
          }}>fitnessforge.ai</div>
          <div style={{
            fontSize: "var(--fs-display)", fontWeight: 900, fontFamily: "var(--d)",
            background: C.gradient, backgroundSize: "300% 100%",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
            lineHeight: 1.1, marginBottom: 8,
          }}>Documents</div>
          <div style={{
            fontSize: "var(--fs-base)", color: C.text3, fontFamily: "var(--m)",
            lineHeight: 1.7, maxWidth: 320,
          }}>
            Magazine-grade PDFs with elite Forge branding. Your program, your progress, your data.
          </div>
        </div>

        {/* Quick stats bar */}
        {hasData && (
          <div style={{
            display: "flex", gap: 0, margin: "20px 16px 0",
            background: C.structGlass, borderRadius: 14,
            border: `1px solid ${C.structBorder}`,
            overflow: "hidden",
          }}>
            {[
              { v: stats.workoutCount, l: "WORKOUTS", c: C.accent },
              { v: prs.length, l: "PRs", c: C.secondary },
              { v: stats.cyclesCompleted, l: "CYCLES", c: C.accent },
              { v: stats.checkInCount, l: "CHECK-INS", c: C.secondary },
            ].map(({ v, l, c }, i) => (
              <div key={l} style={{
                flex: 1, textAlign: "center", padding: "12px 4px",
                borderRight: i < 3 ? `1px solid ${C.structBorder}` : "none",
              }}>
                <div style={{ fontSize: "var(--fs-h3)", fontWeight: 700, color: c, fontFamily: "var(--m)", textShadow: `0 0 12px ${c}30` }}>{v}</div>
                <div style={{ fontSize: 7, color: C.text4, fontFamily: "var(--m)", letterSpacing: ".12em", marginTop: 2 }}>{l}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ═══ DOCUMENT TYPES ═══ */}
      <div style={{ padding: "0 16px 100px" }}>

        {/* ─── FULL PROGRAM — Hero Card ─── */}
        <div
          onClick={() => onNav("pdf")}
          style={{
            cursor: "pointer",
            borderRadius: 18,
            overflow: "hidden",
            background: `linear-gradient(135deg, ${C.accent}0c 0%, ${C.accent}04 50%, ${C.secondary}06 100%)`,
            border: `1.5px solid ${C.accent}20`,
            padding: "24px 22px",
            marginBottom: 14,
            position: "relative",
            transition: "all 0.3s ease",
          }}
        >
          {/* Corner accent */}
          <div style={{
            position: "absolute", top: 0, right: 0, width: 80, height: 80,
            background: `linear-gradient(225deg, ${C.accent}15 0%, transparent 60%)`,
            borderRadius: "0 18px 0 0", pointerEvents: "none",
          }} />
          <div style={{
            position: "absolute", top: 16, right: 18,
            width: 32, height: 32, borderRadius: 9,
            background: C.accent010, border: `1px solid ${C.accent}25`,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="2" strokeLinecap="round"><path d="M9 18l6-6-6-6" /></svg>
          </div>

          <div style={{
            fontSize: 8, fontFamily: "var(--m)", fontWeight: 700,
            letterSpacing: ".2em", color: C.accent, marginBottom: 10,
          }}>COMPLETE PROGRAM</div>
          <div style={{
            fontSize: "var(--fs-h2)", fontWeight: 800, fontFamily: "var(--d)",
            color: C.text1, marginBottom: 6, lineHeight: 1.2,
          }}>Full Program PDF</div>
          <div style={{
            fontSize: "var(--fs-sm)", color: C.text3, fontFamily: "var(--m)",
            lineHeight: 1.6, marginBottom: 16, maxWidth: "85%",
          }}>
            Training split, nutrition plan, supplements, recovery protocols, methodology — select sections to include.
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {["Training", "Nutrition", "Supplements", "Recovery", "Methodology"].map(tag => (
              <div key={tag} style={{
                fontSize: 8, fontFamily: "var(--m)", fontWeight: 600,
                letterSpacing: ".08em", color: C.accent,
                background: C.accent008, border: `1px solid ${C.accent}15`,
                borderRadius: 5, padding: "3px 8px",
              }}>{tag.toUpperCase()}</div>
            ))}
          </div>
        </div>

        {/* ─── TWO-COLUMN: Progress Report + Workout Sheets ─── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>

          {/* Progress Report tile */}
          <div
            onClick={generating ? undefined : generateProgressReport}
            style={{
              cursor: generating === "progress" ? "wait" : "pointer",
              borderRadius: 16, overflow: "hidden",
              background: `linear-gradient(160deg, ${C.secondary}0a 0%, transparent 60%)`,
              border: `1.5px solid ${C.secondary}18`,
              padding: "20px 16px",
              position: "relative", transition: "all 0.3s ease",
            }}
          >
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: `${C.secondary}10`, border: `1px solid ${C.secondary}20`,
              display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14,
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.secondary} strokeWidth="1.5" strokeLinecap="round">
                <path d="M18 20V10" /><path d="M12 20V4" /><path d="M6 20v-6" />
              </svg>
            </div>
            <div style={{ fontSize: 7, fontFamily: "var(--m)", fontWeight: 700, letterSpacing: ".18em", color: C.secondary, marginBottom: 6 }}>ANALYTICS</div>
            <div style={{ fontSize: "var(--fs-md)", fontWeight: 700, fontFamily: "var(--d)", color: C.text1, marginBottom: 4, lineHeight: 1.2 }}>
              {generating === "progress" ? "Generating..." : "Progress Report"}
            </div>
            <div style={{ fontSize: "var(--fs-xs)", color: C.text4, fontFamily: "var(--m)", lineHeight: 1.5 }}>
              {hasData ? `${stats.workoutCount} workouts tracked` : "Start training to unlock"}
            </div>
            {hasData && stats.totalVolumeAllTime > 0 && (
              <div style={{ marginTop: 12, padding: "8px 0 0", borderTop: `1px solid ${C.structBorder}` }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: C.secondary, fontFamily: "var(--m)" }}>
                  {Math.round(stats.totalVolumeAllTime / 1000)}k
                </div>
                <div style={{ fontSize: 7, color: C.text4, fontFamily: "var(--m)", letterSpacing: ".1em" }}>LBS MOVED</div>
              </div>
            )}
          </div>

          {/* Workout Sheets tile */}
          <div
            onClick={() => setSelectedWorkout(selectedWorkout ? null : "show")}
            style={{
              cursor: "pointer", borderRadius: 16, overflow: "hidden",
              background: `linear-gradient(160deg, ${C.accent}08 0%, transparent 60%)`,
              border: `1.5px solid ${C.accent}15`,
              padding: "20px 16px",
              position: "relative", transition: "all 0.3s ease",
            }}
          >
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: C.accent010, border: `1px solid ${C.accent}20`,
              display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14,
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="1.5" strokeLinecap="round">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <line x1="9" y1="3" x2="9" y2="21" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="3" y1="15" x2="21" y2="15" />
              </svg>
            </div>
            <div style={{ fontSize: 7, fontFamily: "var(--m)", fontWeight: 700, letterSpacing: ".18em", color: C.accent, marginBottom: 6 }}>GYM READY</div>
            <div style={{ fontSize: "var(--fs-md)", fontWeight: 700, fontFamily: "var(--d)", color: C.text1, marginBottom: 4, lineHeight: 1.2 }}>Workout Sheets</div>
            <div style={{ fontSize: "var(--fs-xs)", color: C.text4, fontFamily: "var(--m)", lineHeight: 1.5 }}>
              {allTrainingDays.length > 0 ? `${allTrainingDays.length} workouts available` : "No workouts assigned"}
            </div>
            {allTrainingDays.length > 0 && (
              <div style={{ marginTop: 12, padding: "8px 0 0", borderTop: `1px solid ${C.structBorder}` }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: C.accent, fontFamily: "var(--m)" }}>{totalExercises}</div>
                <div style={{ fontSize: 7, color: C.text4, fontFamily: "var(--m)", letterSpacing: ".1em" }}>EXERCISES</div>
              </div>
            )}
          </div>
        </div>

        {/* ─── Expanded Workout Sheet List ─── */}
        {selectedWorkout && allTrainingDays.length > 0 && (
          <div style={{
            borderRadius: 16, border: `1px solid ${C.structBorder}`,
            background: C.structGlass, overflow: "hidden", marginBottom: 14,
          }}>
            <div style={{ padding: "14px 18px 10px", borderBottom: `1px solid ${C.structBorder}` }}>
              <div style={{ fontSize: 8, fontFamily: "var(--m)", fontWeight: 700, letterSpacing: ".16em", color: C.accent }}>SELECT WORKOUT TO PRINT</div>
            </div>
            {allTrainingDays.map((day, i) => (
              <div key={day.d} onClick={generating ? undefined : () => generateWorkoutPDF(day)}
                style={{
                  display: "flex", alignItems: "center", gap: 14,
                  padding: "12px 18px", cursor: generating ? "wait" : "pointer",
                  borderBottom: i < allTrainingDays.length - 1 ? `1px solid ${C.structBorder}` : "none",
                  transition: "background 0.15s",
                }}>
                <div style={{
                  width: 30, height: 30, borderRadius: 8,
                  background: `linear-gradient(135deg, ${C.accent}10, ${C.accent}05)`,
                  border: `1px solid ${C.accent}18`,
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  fontFamily: "var(--m)", fontWeight: 700, fontSize: "var(--fs-xs)", color: C.accent,
                }}>{day.d}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: "var(--fs-sm)", fontWeight: 600, color: C.text1 }}>
                    {generating === "workout-" + day.d ? "Generating..." : day.t}
                  </div>
                  <div style={{ fontSize: "var(--fs-xs)", color: C.text4, fontFamily: "var(--m)" }}>
                    {day.x.length} exercises · ~{day.m} min
                  </div>
                </div>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={C.text4} strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12l7 7 7-7" /></svg>
              </div>
            ))}
          </div>
        )}

        {/* ─── FOUNDER BANNER ─── */}
        <div style={{
          borderRadius: 16, overflow: "hidden", position: "relative",
          background: `linear-gradient(135deg, #0a0a12 0%, #12121e 40%, ${C.accent}08 100%)`,
          border: `1px solid ${C.accent}12`,
          padding: "22px 20px", marginTop: 8,
        }}>
          <div style={{
            position: "absolute", top: 0, right: 0, bottom: 0, width: "40%",
            background: `linear-gradient(270deg, ${C.accent}06 0%, transparent 100%)`,
            pointerEvents: "none",
          }} />
          <div style={{ fontSize: 7, fontFamily: "var(--m)", fontWeight: 700, letterSpacing: ".22em", color: C.accent, marginBottom: 8, opacity: 0.7 }}>BUILT BY</div>
          <div style={{ fontSize: "var(--fs-h3)", fontWeight: 800, fontFamily: "var(--d)", color: "#fff", marginBottom: 4, lineHeight: 1.2 }}>Two-Time Pro World Champion</div>
          <div style={{ fontSize: "var(--fs-sm)", color: "rgba(255,255,255,0.5)", fontFamily: "var(--m)", lineHeight: 1.6, maxWidth: "80%" }}>
            Every protocol, every cue, every system — built from decades on stage and in the trenches.
          </div>
        </div>

        {/* ─── HOW IT WORKS ─── */}
        <div style={{ marginTop: 20, padding: "0 4px" }}>
          <div style={{ fontSize: 8, fontFamily: "var(--m)", fontWeight: 700, letterSpacing: ".18em", color: C.text4, marginBottom: 14 }}>HOW IT WORKS</div>
          {[
            { n: "01", t: "Choose", d: "Pick your document type above" },
            { n: "02", t: "Customize", d: "Select sections to include (Program PDF)" },
            { n: "03", t: "Generate", d: "Print dialog opens — save as PDF" },
          ].map(({ n, t, d }) => (
            <div key={n} style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: C.accent, fontFamily: "var(--m)", opacity: 0.4, lineHeight: 1, minWidth: 20 }}>{n}</div>
              <div>
                <div style={{ fontSize: "var(--fs-sm)", fontWeight: 600, color: C.text2, marginBottom: 1 }}>{t}</div>
                <div style={{ fontSize: "var(--fs-xs)", color: C.text4, fontFamily: "var(--m)" }}>{d}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
